import {
  Activity,
  Boxes,
  FileText,
  Lock,
  Network,
  Radar,
  Rocket,
  Waypoints,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  DiscoveryRow,
  LiveDot,
  MiniStat,
  ModuleRow,
  TreeBranch,
} from "../ScannerParts";
import type { ScannerController } from "../useScannerController";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

// Plain-English narration of the crawl so the live view always reads as
// "making progress", even during the long login/setup phase where the crawler
// reports a high percentage but zero pages yet (which otherwise looks stuck).
const STAGES = [
  "Connecting & signing in",
  "Discovering pages",
  "Mapping modules & navigation",
  "Finalizing analysis",
] as const;

/** Derive which narration stage the crawl is in from the live signals. */
function deriveStageIndex(opts: {
  progress: number;
  pages: number;
  modules: number;
  message: string;
}): number {
  const msg = opts.message.toLowerCase();
  if (
    opts.progress >= 95 ||
    /final|complet|wrap|analy[sz]|grouping|build/.test(msg)
  )
    return 3;
  if (opts.modules > 0) return 2;
  if (opts.pages > 0) return 1;
  return 0;
}

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

/** Seconds since this scanning view mounted (i.e. since the current crawl began). */
function useElapsedSeconds(): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const id = setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, []);
  return seconds;
}

export default function ScanningPhase({
  c,
  onClose,
}: {
  c: ScannerController;
  onClose: () => void;
}) {
  const pct = Math.min(100, Math.round(c.displayProgress));
  const total = c.displayEstimatedTotal;
  const pages = c.displayPagesFound;
  const modules = c.counters.modules;
  const liveMessage = c.statusMessage?.trim() || "";
  const elapsed = useElapsedSeconds();

  const stageIndex = useMemo(
    () =>
      deriveStageIndex({ progress: pct, pages, modules, message: liveMessage }),
    [pct, pages, modules, liveMessage],
  );

  // Before the first page lands, lead with the narrated stage (not a bare,
  // seemingly-stuck "0"). Once pages arrive, the climbing count is the headline.
  const preDiscovery = pages <= 0;
  const headline = preDiscovery ? STAGES[stageIndex] : `${pages}`;
  const subline = preDiscovery
    ? liveMessage || "Setting things up — the first pages will appear shortly…"
    : `${total ? `of ~${total} ` : ""}pages discovered`;

  // Reassurance adapts to how long we've been running so a slow login or a large
  // app doesn't feel like a hang — the crawl keeps streaming in the background.
  const reassurance =
    elapsed < 90
      ? "This usually takes 30–90 seconds. You can keep working — we'll keep scanning."
      : elapsed < 240
        ? "Still exploring — larger apps with many pages take a little longer. Hang tight."
        : "Deep crawl in progress — this is a large app. We're still discovering pages.";

  // Bigger sites can take a while. Let the user detach the live view and let the
  // crawl finish server-side rather than sit and watch it.
  const runInBackground = () => {
    c.runInBackground();
    onClose();
  };

  return (
    <div className="flex flex-1 min-h-full flex-col">
      <div className="flex-1 space-y-4 p-5">
        {/* Hero live status */}
        <Card className="shadow-none relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-info/10 via-transparent to-transparent" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-info/10 blur-3xl" />
          <div className="relative grid gap-5 p-5 md:grid-cols-[1.5fr_1fr]">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <LiveDot />
                <span className="text-[11px] font-medium uppercase tracking-wide text-success">
                  AI agent live
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  on {c.displayHost}
                </span>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                  {formatElapsed(elapsed)} elapsed
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-info/10 text-info ring-1 ring-info/20">
                  <Radar className="h-5 w-5 animate-pulse" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-2xl font-semibold tabular-nums tracking-tight">
                    {headline}
                  </h2>
                  <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">
                    {subline}
                  </span>
                  {/* Always surface the crawler's own live message so the user sees
                      concrete, moving activity (e.g. "Submitting login form…"). */}
                  {!preDiscovery && liveMessage && (
                    <p
                      className="mt-0.5 truncate text-[12px] text-muted-foreground"
                      title={liveMessage}
                    >
                      {liveMessage}
                    </p>
                  )}
                </div>
                <span className="ml-auto hidden shrink-0 items-center gap-1 self-start text-[11px] text-muted-foreground sm:inline-flex">
                  <Waypoints className="h-3 w-3" /> depth-first crawl
                </span>
              </div>

              <div className="space-y-1.5">
                {pct <= 0 ? (
                  <div className="h-2 w-full overflow-hidden rounded-full bg-info/15">
                    <div className="h-full w-2/5 animate-pulse rounded-full bg-info" />
                  </div>
                ) : (
                  <Progress value={pct} className="h-2" />
                )}
                <p className="text-[11px] text-muted-foreground">
                  {reassurance}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Larger sites can take up to{" "}
                  <span className="font-medium text-foreground">
                    15–20 minutes
                  </span>{" "}
                  — click{" "}
                  <button
                    type="button"
                    onClick={runInBackground}
                    className="font-medium text-info underline-offset-2 hover:underline"
                  >
                    Sync in background
                  </button>{" "}
                  and we'll keep crawling while you work.
                </p>
              </div>
            </div>

            {/* Live counters for the crawl (routes / forms / modules / protected). */}
            <div className="self-center">
              <div className="grid grid-cols-4 gap-2">
                <MiniStat
                  label="Routes"
                  value={c.counters.routes}
                  icon={Waypoints}
                />
                <MiniStat
                  label="Forms"
                  value={c.counters.forms}
                  icon={FileText}
                />
                <MiniStat
                  label="Modules"
                  value={c.counters.modules}
                  icon={Boxes}
                />
                <MiniStat
                  label="Protected"
                  value={c.counters.protectedRoutes}
                  icon={Lock}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Main grid */}
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="shadow-none lg:col-span-1">
            <PanelHeader
              icon={Network}
              title="Application map"
              right={`${c.tree.length} groups`}
            />
            <ScrollArea className="h-[300px]">
              <div className="space-y-3 p-4">
                {c.tree.length === 0 ? (
                  <Placeholder text="The map builds itself as pages are discovered…" />
                ) : (
                  c.tree.map((node) => (
                    <TreeBranch key={node.name} node={node} />
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>

          <Card className="shadow-none lg:col-span-1">
            <PanelHeader
              icon={Activity}
              title="Live discoveries"
              right={`${c.discoveries.length} events`}
            />
            <ScrollArea className="h-[300px]">
              <ol className="relative p-4">
                {c.discoveries.length === 0 ? (
                  <li>
                    <Placeholder
                      text={
                        stageIndex === 0
                          ? "Signing in first — pages will stream in here as they're found…"
                          : "Looking for the first pages…"
                      }
                    />
                  </li>
                ) : (
                  c.discoveries.map((d) => (
                    <DiscoveryRow key={d.id} discovery={d} />
                  ))
                )}
              </ol>
            </ScrollArea>
          </Card>

          <Card className="shadow-none lg:col-span-1">
            <PanelHeader
              icon={Boxes}
              title="Modules detected"
              right={`${c.modules.length} found`}
            />
            <ScrollArea className="h-[300px]">
              <div className="space-y-2 p-4">
                {c.modules.length === 0 ? (
                  <Placeholder text="Modules appear once related pages are grouped together…" />
                ) : (
                  c.modules.map((m) => <ModuleRow key={m.name} module={m} />)
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>

      {/* Footer — detach (keep crawling) or stop entirely */}
      <div className="mt-auto sticky bottom-0 z-10 flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-5 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <LiveDot /> Scanning live — can take up to 15–20 min on larger sites.
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={c.cancel}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" /> Stop scan
          </Button>
          <Button size="sm" onClick={runInBackground} className="gap-1.5">
            <Rocket className="h-3.5 w-3.5" /> Sync in background
          </Button>
        </div>
      </div>
    </div>
  );
}

function PanelHeader({
  icon: Icon,
  title,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  right: string;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border p-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-info" />
        <h3 className="text-[13px] font-semibold">{title}</h3>
      </div>
      <span className="text-[11px] text-muted-foreground tabular-nums">
        {right}
      </span>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border p-3 text-[12px] text-muted-foreground">
      <span className="h-2 w-2 animate-pulse rounded-full bg-info" />
      {text}
    </div>
  );
}
